import { EffectSlot } from '../core/project/types';
import { EFFECTS } from './registry';
import { EffectInstance } from './types';

/**
 * EffectChain — a live insert chain between an input and output GainNode.
 *
 * Rebuilds the node graph only when the enabled slot signature changes
 * (order/type/toggle); param-only changes patch the live instances in place
 * so knob drags stay cheap.
 */
export class EffectChain {
  readonly input: GainNode;
  readonly output: GainNode;

  private instances: EffectInstance[] = [];
  private sig = '__empty__';

  constructor(private readonly ctx: BaseAudioContext) {
    this.input = ctx.createGain();
    this.output = ctx.createGain();
    this.input.connect(this.output);
  }

  setSlots(slots: EffectSlot[]): void {
    const active = slots.filter((s) => s.enabled && EFFECTS[s.type]);
    const sig = active.map((s) => `${s.type}:${s.id}`).join('|') || '__empty__';

    if (sig === this.sig) {
      // Param-only change: patch in place.
      for (const inst of this.instances) {
        const slot = slots.find((s) => s.id === inst.slotId);
        if (slot) inst.update(slot.params);
      }
      return;
    }

    this.rebuild(slots, active, sig);
  }

  private rebuild(slots: EffectSlot[], active: EffectSlot[], sig: string): void {
    // Tear down.
    for (const inst of this.instances) inst.dispose();
    this.instances = [];
    try {
      this.input.disconnect();
    } catch {
      /* noop */
    }

    if (active.length === 0) {
      this.input.connect(this.output);
      this.sig = sig;
      return;
    }

    let node: AudioNode = this.input;
    for (const slot of active) {
      const def = EFFECTS[slot.type];
      const built = def.build(this.ctx, slot.params);
      const inst: EffectInstance = { slotId: slot.id, ...built };
      node.connect(built.input);
      node = built.output;
      this.instances.push(inst);
    }
    node.connect(this.output);
    this.sig = sig;
    void slots;
  }

  dispose(): void {
    for (const inst of this.instances) inst.dispose();
    this.instances = [];
    this.input.disconnect();
    this.output.disconnect();
  }
}